import React, { useMemo } from 'react';
import KpiRow, { KpiTile } from '../components/dashboard/KpiRow';
import PipelineBand from '../components/dashboard/PipelineBand';
import AttentionList from '../components/dashboard/AttentionList';
import RFQTable from '../components/dashboard/RFQTable';
import FirstRunState from '../components/dashboard/empty/FirstRunState';
import DashboardWelcome from '../components/dashboard/welcome/DashboardWelcome';
import { useDashboard, ACTIVE_PIPELINE_STAGES, DashboardKpis, AttentionItem } from '../hooks/useDashboard';
import { useDashboardWelcome } from '../hooks/useDashboardWelcome';
import { useResearchProgress } from '../hooks/useResearchProgress';
import { Skeleton } from '../components/ui';
import { useAuth } from '../contexts/AuthContext';
import ShellPage from '../layout/ShellPage';

interface DashboardProps {
  onCreateRFQ: () => void;
  onViewRFQ: (rfqId: string) => void;
}

/** Mock legend labels for the five active stages, in ramp order. */
const STAGE_LABELS = ['Upload BOM', 'Criteria', 'Discovery', 'Outreach', 'Quotes & Proposals'];

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** KPI tiles from real fetched data only — unavailable metrics render "—". */
const buildTiles = (k: DashboardKpis): KpiTile[] => [
  {
    label: 'Active RFQs',
    value: String(k.activeTotal),
    sub: k.activeTotal > 0 ? `across ${plural(k.stagesInPlay, 'stage')}` : 'none in flight yet',
  },
  {
    label: 'Awaiting your review',
    value: String(k.awaitingReview),
    tone: k.awaitingReview > 0 ? 'action' : 'ink',
    sub:
      k.awaitingReview > 0 && k.oldestWaitingDays !== null
        ? k.oldestWaitingDays === 0
          ? 'oldest arrived today'
          : `oldest waiting ${plural(k.oldestWaitingDays, 'day')}`
        : 'nothing waiting on you',
  },
  {
    label: 'Suppliers engaged',
    value: k.suppliersEngaged !== null ? String(k.suppliersEngaged) : null,
    sub:
      k.suppliersEngaged === null
        ? 'no outreach data yet'
        : k.suppliersEngaged === 0
          ? 'no outreach sent yet'
          : k.suppliersRespondedThisWeek !== null && k.suppliersRespondedThisWeek > 0
            ? `${k.suppliersRespondedThisWeek} responded this week`
            : 'none responded this week',
  },
  {
    label: 'Avg. sourcing cycle',
    value: k.avgCycleDays !== null ? k.avgCycleDays.toFixed(1) : null,
    unit: 'days',
    // A delta only exists when both 90-day windows hold completed RFQs —
    // otherwise the honest sub is the sample size, never a fabricated trend.
    sub:
      k.avgCycleDays === null
        ? 'no completed RFQs yet'
        : k.avgCycleDeltaPct !== null
          ? `${k.avgCycleDeltaPct <= 0 ? '↓' : '↑'} ${Math.abs(k.avgCycleDeltaPct)}% vs last quarter`
          : `across ${plural(k.completedCount, 'completed RFQ')}`,
    subTone: k.avgCycleDeltaPct !== null && k.avgCycleDeltaPct < 0 ? 'success' : 'muted',
  },
];

const Dashboard: React.FC<DashboardProps> = ({ onCreateRFQ, onViewRFQ }) => {
  const {
    filteredRfqs, attentionItems, stats, loading,
    ownerView, setOwnerView, stageFilter, setStageFilter,
  } = useDashboard();
  const { currentUser, backendUser } = useAuth();
  const welcome = useDashboardWelcome(stats.myTotal);
  const discoveryRows = useMemo(
    () => filteredRfqs.filter((r) => r.stageStep === 3).map((r) => ({ id: r.id, bomId: r.bomId ?? null })),
    [filteredRfqs],
  );
  const research = useResearchProgress(discoveryRows);

  // Every attention action continues in the wizard at the RFQ's current step.
  const handleAttentionAction = (item: AttentionItem) => onViewRFQ(item.id);

  // Robbie greets by first name only — the full name reads like a form field.
  const userName = backendUser?.fullName || currentUser?.displayName || currentUser?.email?.split('@')[0] || 'there';
  const firstName = userName.trim().split(/\s+/)[0];

  const tiles = useMemo(() => buildTiles(stats.kpis), [stats]);
  const pipelineStages = useMemo(
    () =>
      ACTIVE_PIPELINE_STAGES.map((stage, i) => ({
        label: STAGE_LABELS[i],
        count: stats.pipelineCounts[stage] || 0,
      })),
    [stats],
  );

  if (loading) {
    return (
      <ShellPage title="Overview" activeId="overview" onCreateRFQ={onCreateRFQ}>
        <div aria-busy="true" aria-label="Loading your workspace" className="flex flex-col gap-[16px]">
          <div className="grid grid-cols-2 gap-s5 md:grid-cols-4">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} shape="block" height={92} />)}
          </div>
          <Skeleton shape="block" height={92} />
          <RFQTable rows={[]} ownerView={ownerView} onOwnerViewChange={setOwnerView} stageFilter={stageFilter} onStageFilterChange={setStageFilter} counts={{ all: 0, needsReview: 0 }} loading onRowClick={() => undefined} />
        </div>
      </ShellPage>
    );
  }

  // First run: nothing exists yet, so the search box says so rather than
  // offering to look through an empty org.
  if (stats && stats.total === 0) {
    return (
      <ShellPage
        title="Overview"
        activeId="overview"
        rfqCount={0}
        onCreateRFQ={onCreateRFQ}
        searchPlaceholder="Nothing to search yet"
      >
        <FirstRunState firstName={firstName} />
      </ShellPage>
    );
  }

  return (
    // Sidebar badge reads the same org-wide summary number as the tiles —
    // the shell never contradicts the page.
    <ShellPage title="Overview" activeId="overview" rfqCount={stats.kpis.activeTotal} onCreateRFQ={onCreateRFQ}>
      <div className="flex flex-col gap-[16px] pb-12">
        {/* The welcome sits above the dashboard and never replaces it: the
            workspace's numbers stay readable underneath, because the person
            reading them may have come for exactly those. */}
        {welcome.visible && (
          <DashboardWelcome
            firstName={firstName}
            activeTotal={stats.kpis.activeTotal}
            dismissed={welcome.dismissed}
            onStart={onCreateRFQ}
            onDismiss={welcome.dismiss}
            dismissing={welcome.dismissing}
            dismissFailed={welcome.dismissFailed}
          />
        )}

        {/* Two altitudes on one page, each labelled: the tiles are the whole
            workspace and stay that way whatever the table is filtered to —
            a finance reader glancing at them would otherwise lose the exact
            number they came for. */}
        <div className="flex flex-col gap-s3">
          <span className="text-rail-label uppercase text-ink-muted">Across the workspace</span>
          <KpiRow tiles={tiles} />
        </div>
        <PipelineBand stages={pipelineStages} awardedThisMonth={stats.kpis.awardedThisMonth} />

        <AttentionList items={attentionItems} onAction={handleAttentionAction} />

        <RFQTable
          rows={filteredRfqs}
          ownerView={ownerView}
          onOwnerViewChange={setOwnerView}
          stageFilter={stageFilter}
          onStageFilterChange={setStageFilter}
          counts={stats.filterCounts}
          research={research}
          onRowClick={onViewRFQ}
        />
      </div>
    </ShellPage>
  );
};

export default Dashboard;
